import { ExternalLink, Trash2 } from 'lucide-react'
import { type InvoiceRow, getInvoicePublicUrl } from 'src/api/db/apiInvoice'
import { useSupabaseClient } from 'src/api/supabase'
import { formatDate } from 'src/shared/utils'
import { Card } from 'src/shared/components/ui'
import { Button } from 'src/shared/components/ui/button'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { isPdfUrl } from 'src/shared/utils/proofUrls'
import { cn } from 'src/shared/components/ui/utils'

function isImageFilename(name: string): boolean {
  return /\.(png|jpe?g|webp)$/i.test(name)
}

export const InvoiceList = ({
  invoices,
  canDelete,
  onRequestDelete,
  fullBleed = false,
}: {
  invoices: InvoiceRow[]
  canDelete: boolean
  onRequestDelete: (row: InvoiceRow) => void
  fullBleed?: boolean
}) => {
  const { t } = useLocalization()
  const supabase = useSupabaseClient()

  return (
    <Column className={cn(fullBleed ? 'divide-y divide-gray-200' : 'gap-4')}>
      {invoices.map((inv) => {
        const href = getInvoicePublicUrl(supabase, inv.storage_path)
        const isPdf =
          isPdfUrl(href) || inv.file_name.toLowerCase().endsWith('.pdf')
        const isImage = !isPdf && isImageFilename(inv.file_name)

        const header = (
          <Row className="flex-wrap items-center justify-between gap-4">
            <Column className="min-w-0 gap-1">
              <span className="block truncate font-medium">{inv.file_name}</span>
              <p className="text-sm text-muted-foreground">
                {formatDate(inv.created_at)}
              </p>
            </Column>

            <Row className="shrink-0 items-center gap-2">
              <Button variant="ghost" size="sm" asChild>
                <a href={href} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="mr-2 h-4 w-4" aria-hidden />
                  {t('Open in new tab', 'Öppna i ny flik')}
                </a>
              </Button>

              {canDelete && (
                <Button
                  variant="destructive"
                  size="sm"
                  type="button"
                  onClick={() => onRequestDelete(inv)}
                  aria-label={t('Delete invoice', 'Ta bort faktura')}
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </Button>
              )}
            </Row>
          </Row>
        )

        const preview = isPdf ? (
          <iframe
            src={href}
            title={inv.file_name}
            className={cn(
              'w-full bg-gray-50',
              fullBleed
                ? 'h-[min(80vh,900px)]'
                : 'h-[min(70vh,800px)] rounded border border-gray-200'
            )}
          />
        ) : isImage ? (
          <img
            src={href}
            alt={inv.file_name}
            className={cn(
              'w-full bg-gray-50 object-contain',
              fullBleed
                ? 'max-h-[min(80vh,900px)]'
                : 'max-h-[min(70vh,800px)] rounded border border-gray-200'
            )}
          />
        ) : (
          <Button variant="secondary" size="sm" asChild>
            <a href={href} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="mr-2 h-4 w-4" aria-hidden />
              {t('Download file', 'Ladda ner fil')}
            </a>
          </Button>
        )

        if (fullBleed) {
          return (
            <Column key={inv.id} className="gap-4 pb-6 pt-4">
              <div className="px-4">{header}</div>
              {preview}
            </Column>
          )
        }

        return (
          <Card
            key={inv.id}
            className="overflow-hidden border border-gray-200 p-4"
          >
            <Column className="gap-4">
              {header}
              {preview}
            </Column>
          </Card>
        )
      })}
    </Column>
  )
}
